import { useState } from 'react'
import { useForm, Controller, useWatch } from "react-hook-form"
import { Settings, Zone } from 'luxon'
const { findWindows } = await import(
  /* webpackChunkName: "windowsiana" */ 'windows-iana'
);

import Select, { Option } from './common/select'
import ProcessingRing from './common/processing-ring'
import { Message, MessageType } from './common/message'
import { fetcher, ContentType, RequestMethod } from "../utils/fetcher"

type Props = {
  loginUrl: string
  registerUrl: string
  validateAccountUrl: string
  timezoneOptions: { value: string; label: string, offset: number }[]
  minPasswordLength: number
  licensePack: {
    ownerAttendeeDisplayName: string
    accountTypes: number
  }
  token: string
  isHostedInstall: boolean
}

type ValidationResult = {
  noPassword: boolean
  userNameTaken: boolean
  alreadyRegistered: boolean
  alreadyPurchased: boolean
  passwordMismatch: boolean
  passwordInvalid: boolean
  needsTimezone: boolean
  EmailValid: boolean
  licensePackInvitation: string
  LicensePackOwner: string
  AlreadyTried: boolean
  block: boolean
}

type LicensePackageMessage = {
  owner: string
  link: string
}

export default function RegisterComponent(props: Props): JSX.Element {
  const { register, trigger, handleSubmit, getValues, control, setError, formState: { errors, isValid } } = useForm({ mode: "onBlur", reValidateMode: "onBlur" })
  const [LicensePackageMessage, setLicensePackageMessage] = useState<LicensePackageMessage>()
  const [analyticsId, setAnalyticsId] = useState<string>()
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setLoading] = useState(false)

  const validateEmail = async (email) => {
    await trigger("email")

    try {
      const validationResult: ValidationResult = await fetcher(props.validateAccountUrl, {
        method: RequestMethod.POST,
        contentType: ContentType.FormUrlEncoded,
        body: getValues(),
      })

      if (!validationResult.EmailValid && email) {
        if (validationResult.block) {
          setError("email", {
            type: "manual",
            message: "Please contact support, there is a problem with your email address."
          })
        } else {
          setError("email", {
            type: "manual",
            message: "Please enter a valid email address."
          })
        }
      }

      if ((validationResult.alreadyPurchased || validationResult.alreadyRegistered) && email) {
        setError("email", {
          type: "manual",
          message: "You've already registered with this email address."
        })

        setLicensePackageMessage(null)
      } else if (validationResult.EmailValid && email) {
        if (validationResult.licensePackInvitation && !props.licensePack) {
          setLicensePackageMessage({
            owner: validationResult.LicensePackOwner,
            link: validationResult.licensePackInvitation + "&email=" + encodeURIComponent(email)
          })
        } else {
          setLicensePackageMessage(null)
        }
      }
    } catch (response) {
      const retryAfter = response.headers.get("retry-after")

      if (response.status == 429 && retryAfter) {
        const seconds = retryAfter % 60
        const minutes = Math.round((retryAfter - seconds) / 60)

        let retryText = ""

        if (minutes > 0) {
          retryText += ` ${minutes} minutes`
        }

        if (seconds > 0) {
          retryText += ` ${seconds} seconds`
        }

        setError("email", {
          type: "manual",
          message: `Sorry, you’ve tried this too many times. Please try again in${retryText}`
        })
      }
    }
  }

  const onPasswordBlur = async () => {
    await trigger("password")
  }

  const onConfirmPasswordBlur = async () => {
    await trigger("confirmPassword")
  }

  const onSubmit = async ({ email }) => {
    if (isLoading) {
      return
    }

    setLoading(true)
    if (window.analytics) {
      setAnalyticsId(window.analytics.getUserId())
    }

    await validateEmail(email)

    if (!isValid) {
      setLoading(false)
      return
    }

    if (window.analytics) {
      window.analytics.alias(email.toLowerCase())
    }

    const body = getValues()

    if (props.token) {
      body["token"] = props.token
    }

    if (props.licensePack?.accountTypes) {
      body["accountType"] = props.licensePack?.accountTypes
    }

    try {
      const { redirectUrl }: { redirectUrl: string } = await fetcher(props.registerUrl, {
        method: RequestMethod.POST,
        contentType: ContentType.FormUrlEncoded,
        body
      })

      location.href = redirectUrl
    }
    catch (error) {
      Object.keys(getValues()).forEach(fieldName => {
        if (error.data[fieldName]) {
          setError(fieldName, {
            type: "manual",
            message: error.data[fieldName]
          })
        }
      })

      if (error.data['_FORM'] || error.data['Page']) {
        const errors = (error.data['_FORM'] || []).concat(error.data['Page'] || [])

        setMessages(errors.map(message => ({
          text: message,
          type: MessageType.Error
        })))
      }

      setLoading(false)
    }
  }

  const ianaTimezone = (Settings.defaultZone as Zone).name
  const windowsTimezone = findWindows(ianaTimezone)[0]
  let activeTimezone = props.timezoneOptions.find(x => x.value === windowsTimezone)

  if (!activeTimezone) {
    activeTimezone = props.timezoneOptions.find(x => x.offset === new Date().getTimezoneOffset() / -60);
  }

  const password = useWatch({ control, name: 'password' });

  return (
    <>
      <div className="w-full text-xl font-bold text-center">Get Started with Less Meeting</div>
      {messages.map((message, index) => <div key={index} className={`${message.type == MessageType.Success ? 'success-message' : 'error-message'} mb-2`}>
        <div dangerouslySetInnerHTML={{ __html: message.text }}></div>
      </div>)}
      {props.licensePack && !props.isHostedInstall ? <div className="mt-4 text-secondary font-bold">You are joining the license pack owned by {props.licensePack.ownerAttendeeDisplayName}. Welcome to Less Meeting!</div> : null}
      {props.licensePack ? <div className="mt-4"><b>NOTE:</b> if you already have an account, you'll need to <a className="font-bold text-primary" href={props.loginUrl}> Log In</a> to join this license pack.</div> : null}
      {/*<div className="grid grid-cols-3 gap-5 mt-10">
        <button className="flex items-center p-3 text-sm font-semibold border border-gray-600 rounded-lg"><img src="/src/images/slack.svg" className="mr-2" />Sign in With <span className="ml-1 font-black">Slack</span></button>
        <button className="flex items-center p-3 text-sm font-semibold border border-gray-600 rounded-lg"><img src="/src/images/google.svg" className="mr-2" />Sign in With <span className="ml-1 font-black">Google</span></button>
        <button className="flex items-center p-3 text-sm font-semibold border border-gray-600 rounded-lg"><img src="/src/images/microsoft.svg" className="mr-2" />Sign in With <span className="ml-1 font-black">Microsoft</span></button>
  </div>
      <div className="mt-8 text-lg font-bold text-center text-secondary">Or Sign up below</div>*/}
      <form className="mt-8" onSubmit={handleSubmit(onSubmit)}>
        <input type="hidden" name="analyticsId" value={analyticsId || ""} />
        <input type="hidden" name="tz-offset" value={-(new Date()).getTimezoneOffset() / 60} />
        <div className="mt-4">
          <div className="mb-2">Work Email</div>
          <input
            type="text"
            placeholder="Work Email"
            defaultValue=""
            {...register('email', {
              required: "Please enter a valid email address.",
              maxLength: {
                value: 320,
                message: "Email addresses need to be less than 320 characters"
              }
            })}
            onBlur={e => validateEmail(e.target.value)}
            className={errors.email ? "has-error" : ""} />
          {LicensePackageMessage && <div className="text-xs">
            You've been invited to join a license pack (by <span>{LicensePackageMessage.owner}</span>)!  Click <a className="ml-1 font-bold text-primary" href={LicensePackageMessage.link}>here</a> to join.
          </div>}
          {/* @ts-expect-error https://github.com/react-hook-form/react-hook-form/issues/9185 */}
          {errors.email ? <div className="error-text">{errors.email.message}</div> : null}
        </div>
        <div className="mt-4">
          <div className="mb-2">Full Name</div>
          <input
            type="text"
            defaultValue=""
            placeholder="Full Name"
            {...register('username', {
              required: "Please specify your full name."
            })}
            className={errors.username ? "has-error" : ""} />
          {/* @ts-expect-error https://github.com/react-hook-form/react-hook-form/issues/9185 */}
          {errors.username ? <div className="error-text">{errors.username.message}</div> : null}
        </div>
        <div className="mt-4">
          <div className="mb-2">Password</div>
          <input
            type="password"
            defaultValue=""
            placeholder="Password"
            {...register('password', {
              required: `Passwords must be ${props.minPasswordLength} characters minimum.`,
              minLength: {
                value: props.minPasswordLength,
                message: `Passwords must be ${props.minPasswordLength} characters minimum.`
              }
            })}
            onBlur={onPasswordBlur}
            autoComplete="off"
            className={errors.password ? "has-error" : ""} />
          {/* @ts-expect-error https://github.com/react-hook-form/react-hook-form/issues/9185 */}
          {errors.password ? <div className="error-text">{errors.password.message}</div> : null}
        </div>
        <div className="mt-4">
          <div className="mb-2">Confirm Password</div>
          <input
            type="password"
            defaultValue=""
            placeholder="Confirm Password"
            onBlur={onConfirmPasswordBlur}
            {...register('confirmPassword', {
              validate: value => value && value === password || 'Passwords need to match'
            })}
            autoComplete="off"
            className={errors.confirmPassword ? "has-error" : ""} />
          {/* @ts-expect-error https://github.com/react-hook-form/react-hook-form/issues/9185 */}
          {errors.confirmPassword ? <div className="error-text">{errors.confirmPassword.message}</div> : null}
        </div>
        <div className="mt-4">
          <div className="mb-2">Your Time Zone</div>
          <Controller
            name="TimeZoneCode"
            control={control}
            defaultValue={activeTimezone?.value || ""}
            rules={{ required: "Please specify your timezone." }}
            render={({ field: { onChange, value, name } }) => (<>
              <Select
                placeholder="Your Time Zone"
                options={props.timezoneOptions}
                value={props.timezoneOptions.find(option => option.value == value)}
                onChange={(option: Option) => onChange(option.value)}
                className={errors.TimeZoneCode ? "has-error" : ""} />
              <input type="hidden" name={name} value={value} />
            </>)}
          />
          {/* @ts-expect-error https://github.com/react-hook-form/react-hook-form/issues/9185 */}
          {errors.TimeZoneCode ? <div className="error-text">{errors.TimeZoneCode.message}</div> : null}
        </div>
        {Object.keys(errors).length ? <p className="mt-4 text-alert">Please complete/fix all required fields (with red outlines).</p> : null}

        <div className="mt-8 text-xs">
          By submitting this form you agree to the
          <a className="ml-1 font-bold text-primary hover:underline" target="_blank" href="https://lessmeeting.com/terms">Terms of Service</a> and
          <a className="ml-1 font-bold text-primary hover:underline" target="_blank" href="https://lessmeeting.com/privacy">Privacy Policy</a>
        </div>

        <div className="flex items-center w-full mt-4">
          <input
            type="submit"
            id="final-registration-button"
            className="button-primary"
            value="Sign Up" />
          <div className="w-full ml-8 text-right">
            <a className="text-xs font-bold text-primary hover:underline" href={props.loginUrl}>Already have an account?</a>
          </div>
        </div>
        {isLoading ? <div className="h-12 mt-4"><ProcessingRing isLoading={true} size={10}></ProcessingRing></div> : null}
      </form>
    </>
  );
}
